const AMBIENT_GAIN = 0.045;
const AMBIENT_BASE_FREQ = 110;
const RAMP_SECONDS = 1.2;

/** Per-tier (0=ruin .. 4=clean) filter cutoff and detune spread for the ambient pad. */
const TIER_SOUND = [
  { cutoff: 260, detune: 3 },
  { cutoff: 420, detune: 5 },
  { cutoff: 620, detune: 8 },
  { cutoff: 900, detune: 11 },
  { cutoff: 1300, detune: 14 },
];

export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private masterGain: GainNode | null = null;
  private ambientOscillators: OscillatorNode[] = [];
  private ambientFilter: BiquadFilterNode | null = null;
  private muted = false;

  constructor() {
    const unlock = () => {
      void this.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.context = new AudioContextClass();
    await this.context.resume();
    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = this.muted ? 0 : 1;
    this.masterGain.connect(this.context.destination);
    this.unlocked = true;
  }

  correct(): void {
    this.chime([523, 659, 784], 0.09);
  }

  incorrect(): void {
    this.chime([392, 311], 0.16);
  }

  /** Starts a very quiet, continuous procedural pad — safe to call once after unlock(). */
  startAmbient(): void {
    if (!this.context || !this.masterGain || this.ambientOscillators.length > 0) return;

    const ambientGain = this.context.createGain();
    ambientGain.gain.value = AMBIENT_GAIN;

    const filter = this.context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = TIER_SOUND[2].cutoff;
    this.ambientFilter = filter;

    const detunes = [0, TIER_SOUND[2].detune, -TIER_SOUND[2].detune];
    for (const detune of detunes) {
      const oscillator = this.context.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = AMBIENT_BASE_FREQ;
      oscillator.detune.value = detune * 10;
      oscillator.connect(filter);
      oscillator.start();
      this.ambientOscillators.push(oscillator);
    }

    filter.connect(ambientGain).connect(this.masterGain);
  }

  /** @param tierIndex 0 (ruin) .. 4 (clean) — see scene/tiers.ts */
  setTier(tierIndex: number): void {
    if (!this.context || !this.ambientFilter) return;
    const sound = TIER_SOUND[Math.max(0, Math.min(TIER_SOUND.length - 1, tierIndex))];
    const now = this.context.currentTime;
    this.ambientFilter.frequency.setTargetAtTime(sound.cutoff, now, RAMP_SECONDS);
    this.ambientOscillators.forEach((oscillator, index) => {
      const sign = index === 0 ? 0 : index === 1 ? 1 : -1;
      oscillator.detune.setTargetAtTime(sign * sound.detune * 10, now, RAMP_SECONDS);
    });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain && this.context) {
      this.masterGain.gain.setTargetAtTime(muted ? 0 : 1, this.context.currentTime, 0.05);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  private chime(frequencies: number[], stepDuration: number): void {
    if (!this.context || !this.masterGain || this.context.state !== 'running') return;
    const now = this.context.currentTime;

    frequencies.forEach((frequency, index) => {
      const oscillator = this.context!.createOscillator();
      const gain = this.context!.createGain();
      const start = now + index * stepDuration;

      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.09, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + stepDuration);
      oscillator.connect(gain).connect(this.masterGain!);
      oscillator.start(start);
      oscillator.stop(start + stepDuration + 0.02);
    });
  }

  dispose(): void {
    for (const oscillator of this.ambientOscillators) oscillator.stop();
    this.ambientOscillators = [];
    this.ambientFilter = null;
    void this.context?.close();
    this.context = null;
  }
}
